import capitalize from "lodash/capitalize";
import TurndownService from "turndown";
import type { ZendeskConversationMessage, ZendeskTicket } from "../types";

const turndownService = new TurndownService();

/**
 * Converts one Zendesk conversation entry into the markdown body that Linear stores on attachments and comments.
 *
 * @param msg The Zendesk conversation message to normalize.
 * @returns The markdown representation of the message body and its attachments.
 */
export const formatMessage = (msg: ZendeskConversationMessage): string => {
  const content = msg.message.content;
  const description = content ? turndownService.turndown(msg.message.content) || "" : "";
  const attachments = msg.attachments
    .map(
      attachment =>
        `${attachment.contentType.startsWith("image") ? "!" : ""}[${attachment.filename}](${attachment.contentUrl})\n\n`
    )
    .join("\n");

  return attachments.length > 0 ? `${description}\n\n${attachments}` : description;
};

/**
 * Builds the attachment title that identifies the Zendesk ticket inside Linear.
 *
 * @param ticket The Zendesk ticket being attached.
 * @returns The ticket title shown on the Linear attachment.
 */
export const formatTicketTitle = (ticket: ZendeskTicket): string => {
  return `${capitalize(ticket.type)} #${ticket.id}`;
};

/**
 * Build ticket URL from the ticket data.
 *
 * @param subdomain The Zendesk subdomain for the given Zendesk instance.
 * @param ticketId The Zendesk ticket ID.
 * @returns The ticket URL.
 */
export const formatTicketUrl = (subdomain: string, ticketId: string): string => {
  return `https://${subdomain}.zendesk.com/tickets/${ticketId}`;
};

/**
 * Maps a Zendesk ticket into the attachment payload expected by Linear attachment mutations.
 *
 * @param ticket The Zendesk ticket to convert.
 * @returns The attachment title, subtitle, URL, and metadata sent to Linear.
 */
export function attachmentForZendeskConversation(ticket: ZendeskTicket) {
  return {
    title: formatTicketTitle(ticket),
    subtitle: ticket.subject,
    url: ticket.url,
    metadata: {
      id: ticket.id,
      title: ticket.subject,
      priority: ticket.priority,
      messages: ticket.firstMessage ? [ticket.firstMessage] : [],
      attributes: [
        {
          name: "Type",
          value: capitalize(ticket.type),
        },
        {
          name: "Priority",
          value: ticket.priority,
        },
      ],
    },
  };
}
